import { SubscriptionPlan, WorkspaceRole, WorkspaceSeatType } from 'models/user'
import { Stack, Typography } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { APIRequestErrorType } from 'constants/API'
import { AddMembersRole } from 'components/pages/SubscriptionFlow/Steps/AddMembers/AddMembersRole.module'
import BlockInfo from 'components/common/BlockInfo/BlockInfo'
import { KeyboardEventKey } from 'constants/misc'
import { MUIButton } from 'components/common/MUIButton'
import Modal from 'components/common/Modals/Modal/Modal'
import { MultiEmailInput } from 'components/common/MultiEmailInput'
import { TIMEOUT_AFTER_SUCCESSFUL_CALLBACK } from 'constants/application'
import { UserAsMemberUsingEmailDTO } from 'models/workspaces'
import { WORKSPACE_MEMBER_LIMIT_BY_PLAN } from 'constants/workspaces'
import classnames from 'classnames'
import { decideErrorType } from 'utils/APIDecideErrorType'
import { getPricingUrl } from 'utils/localization'
import i18n from 'translations/i18n'
import { logger } from 'utils/logging'
import popupStyles from 'components/styles/popup.module.sass'
import styles from './AddMembersPopup.module.sass'
import { useAddWorkspaceMembers } from 'dataQueries'
import { useAllowedSeatTypes } from 'utils/hooks'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useUserData } from 'components/contexts/UserDataContext'
import { useWorkspacesProfileContext } from '../_main/WorkspacesProfile.context'

/**
 * Builds the add-members invite payload for a chosen seat type (ISSUE-2078). Sends the explicit
 * `seatType` (consumed by WorkspaceService.resolveInvitationSeatType) plus the legacy
 * `sponsoredMember` flag for backward compatibility. Exported for unit testing.
 */
export const buildInviteMemberPayload = (emails: string[], seatType: WorkspaceSeatType): UserAsMemberUsingEmailDTO[] =>
  emails.map((email) => ({ email, inSubscriptionFlow: false, sponsoredMember: seatType === WorkspaceSeatType.SPONSORED, seatType }))

/**
 * Whether the inviter cannot choose the invited seat type. Two cases:
 * - Any inviter on a RESTRICTED (free) seat — including a Restricted Admin — may only invite Restricted
 *   seats, since a restricted seat holds no paid seat to grant (ISSUE-2488).
 * - A non-admin member on a SPONSORED seat likewise cannot pick the invited seat type (ISSUE-2078 / #2).
 * When locked, {@link resolveInviteSeatType} forces the least-privileged allowed seat type.
 * Exported for unit testing.
 */
export const isInviteSeatTypeLocked = (role: WorkspaceRole | undefined, seatType: WorkspaceSeatType | undefined): boolean => {
  if (seatType === WorkspaceSeatType.RESTRICTED) return true
  return role === WorkspaceRole.MEMBER && seatType === WorkspaceSeatType.SPONSORED
}

/**
 * The seat type actually sent in the invite (ISSUE-2078). When the selector is locked, force the
 * least-privileged allowed seat type (RESTRICTED > SPONSORED > FULL) so a locked invite never
 * provisions a paid FULL seat (#1); otherwise the inviter's own selection. Exported for unit testing.
 */
export const resolveInviteSeatType = (isLocked: boolean, allowedSeatTypes: WorkspaceSeatType[], selectedSeatType: WorkspaceSeatType): WorkspaceSeatType => {
  if (!isLocked) return selectedSeatType
  return [WorkspaceSeatType.RESTRICTED, WorkspaceSeatType.SPONSORED, WorkspaceSeatType.FULL].find((type) => allowedSeatTypes.includes(type)) ?? allowedSeatTypes[0] ?? WorkspaceSeatType.FULL
}

/**
 * @interface Props Input properties
 */
interface Props {
  /** The additional classes to append */
  className?: string
  /** Decides when is modal open */
  isOpen: boolean
  /** Workspace ID */
  workspaceId: string
  /** Onclick action triggered when user clicks outside the .modal-content element */
  onClickOutside?: () => void
  /** onKeyDown action triggered when user presses any key on the keyboard */
  onKeyDown?: (e: React.KeyboardEvent) => unknown
  /** Onclick action triggered when user clicks the close button */
  onClickClose?: () => void
  /** Whether the workspace is a personal workspace */
  isWorkspacePersonal?: boolean
}

/**
 * @component Add members to workspace in a popup
 * @example
 * <AddMembersPopup isOpen={true} workspaceId={workspaceId} />
 */
export const AddMembersPopup: React.FC<Props> = ({
  className = '',
  isOpen,
  workspaceId,
  onClickOutside,
  onKeyDown,
  onClickClose,
  isWorkspacePersonal,
}) => {

  const { t } = useTranslation(['workspace_profile', 'actions', 'add_workspace_members_list'])
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()

  const {
    sortedWorkspaceMembers,
    workspaceMembersEmails,
    workspaceData,
    workspaceDataOfUser,
    pendingInvitations,
    isWorkspaceEnterprise
  } = useWorkspacesProfileContext()

  const { currentUserWorkspace } = useUserData()

  const addWorkspaceMembers = useAddWorkspaceMembers()

  // The inviter's own seat, falling back to the currently selected workspace's seat (ISSUE-2488).
  // `workspaceDataOfUser` is derived from `workspaceData.members`, the array this ticket's backend
  // counterpart hides from restricted seats; if it vanishes, `isInviteSeatTypeLocked(undefined,
  // undefined)` is false and the cap lifts, unlocking paid seat types. `currentUserWorkspace.seatType`
  // (from `/user/me`) is always present and keeps the invite locked to Restricted for a restricted inviter.
  const inviterSeatType = workspaceDataOfUser?.seatType ?? currentUserWorkspace?.seatType

  // Seat types the plan permits are derived once by the shared hook so the invite popup, members
  // table and update dropdowns never drift (ISSUE-2078). `isPlanKnown` is false until the plan
  // resolves; we must not send an explicit seat type before then (the backend trusts it verbatim,
  // bypassing its own plan-based fallback — #3).
  const { allowedSeatTypes, isPlanKnown } = useAllowedSeatTypes(workspaceData?.plan, !!isWorkspacePersonal, inviterSeatType)
  const defaultSeatType = allowedSeatTypes[0] ?? WorkspaceSeatType.FULL

  const addWorkspaceMembersResponseData = addWorkspaceMembers.data
  const isInviteLocked = useMemo(() => isInviteSeatTypeLocked(workspaceDataOfUser?.role, inviterSeatType), [workspaceDataOfUser, inviterSeatType])
  const [emails, setEmails] = useState<string[]>([])
  const [selectedSeatType, setSelectedSeatType] = useState<WorkspaceSeatType>(defaultSeatType)
  // The seat type actually sent: the locked value when the inviter can't choose, otherwise their selection.
  const effectiveSeatType = resolveInviteSeatType(isInviteLocked, allowedSeatTypes, selectedSeatType)

  // Keep the selection valid when the allowed set changes (plan/flag/data load).
  useEffect(() => {
    if (!allowedSeatTypes.includes(selectedSeatType)) setSelectedSeatType(defaultSeatType)
  }, [allowedSeatTypes, selectedSeatType, defaultSeatType])

  const membersCount = useMemo(() => (sortedWorkspaceMembers ? sortedWorkspaceMembers?.length : 0) + emails.length, [sortedWorkspaceMembers, emails])
  const isSubmitButtonDisabled = useMemo(() => emails.length === 0 || addWorkspaceMembers.isPending || !isPlanKnown, [emails, addWorkspaceMembers.isPending, isPlanKnown])


  const handleInviteMembers = useCallback(() => {
    const newMembers = buildInviteMemberPayload(emails, effectiveSeatType)

    // Logs the invite request (fires before the mutation resolves), not a confirmed invite — success
    // and failure are reported separately from the mutation result effect below.
    logger.info('Workspace members invite requested', undefined, { sentry: true, context: { component: 'AddMembersPopup', action: 'inviteMembers', metadata: { workspaceId, seatType: effectiveSeatType, count: emails.length } } })

    addWorkspaceMembers.mutate({ workspaceId, members: newMembers })

  }, [addWorkspaceMembers, emails, workspaceId, effectiveSeatType])

  const onOutsideOrClose = useCallback(() => {
    onClickOutside?.()
    onClickClose?.()
    addWorkspaceMembers.reset()
    setEmails([])
    setSelectedSeatType(defaultSeatType)
  }, [addWorkspaceMembers, onClickOutside, onClickClose, defaultSeatType])

  const onKeyboardEvent = useCallback((e: React.KeyboardEvent) => {
    if (onKeyDown) onKeyDown(e)
    if (onKeyDown && e.key === KeyboardEventKey.ESCAPE) {
      addWorkspaceMembers.reset()
      setEmails([])
      setSelectedSeatType(defaultSeatType)
    }
  }, [addWorkspaceMembers, onKeyDown, defaultSeatType])

  const responseError = addWorkspaceMembers.error
  const pendingInvitationsEmailsSet = new Set(pendingInvitations?.map((invitation) => { return invitation.email }))

  // Show toast messages for success and error
  useEffect(() => {
    if (addWorkspaceMembers.isSuccess) {
      const successMessage = !!addWorkspaceMembersResponseData?.invitedUsers.some((invitedUser) => !invitedUser.userId)
        ? t('add_members_success_non_existing_user')
        : t('add_members_success')
      spawnSuccessToast(successMessage)
      window.setTimeout(() => {
        addWorkspaceMembers.reset()
        setEmails([])
        setSelectedSeatType(defaultSeatType)
        onClickClose?.()
      }, TIMEOUT_AFTER_SUCCESSFUL_CALLBACK)
    }
    if (addWorkspaceMembers.isError) {
      logger.error('Failed to add workspace members', addWorkspaceMembers.error, { context: { component: 'AddMembersPopup', metadata: { workspaceId } } })
      const errorMessage = responseError && decideErrorType(responseError) === APIRequestErrorType.PAYMENT_REQUIRED_ERROR
        ? t('error_payment_required')
        : t('add_members_error')
      spawnErrorToast(errorMessage)
      addWorkspaceMembers.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addWorkspaceMembers.isSuccess, addWorkspaceMembers.isError, addWorkspaceMembers.error])

  const onEmailValidation = (inputString: string) => {
    if (pendingInvitationsEmailsSet.has(inputString)) {
      return 'multi_email_input:already_pending_invitations'
    }
  }

  return (
    <Modal
      className={classnames(styles.addMembersPopup, className)}
      isOpen={isOpen}
      modalContentClassName={styles.content}
      onKeyDown={onKeyboardEvent}
      onClose={onOutsideOrClose}
      title={t('invite_members_popup')}
      footerContent={
        <Stack gap={.8} flexDirection="row" justifyContent="flex-end" flexWrap="wrap" marginBottom="0.8rem">

          <MUIButton type="secondaryNoBorder" onClick={onOutsideOrClose}>
            {t('actions:Cancel')}
          </MUIButton>

          <MUIButton onClick={handleInviteMembers} disabled={isSubmitButtonDisabled} isLoading={addWorkspaceMembers.isPending}>
            {t('send_invitation')}
          </MUIButton>

        </Stack>
      }
      queryData={addWorkspaceMembers}
    >

      <div className={popupStyles.inside}>

        <Stack gap={1.6} marginBottom={2.4} marginTop={.7}>

          <MultiEmailInput
            emails={emails}
            previouslyAddedEmails={workspaceMembersEmails}
            onChange={(emails) => setEmails(emails)}
            title={<Typography variant="text-md" fontWeight={'medium'}>{t('invite_by_email')}</Typography>}
            onEmailValidation={onEmailValidation}
          />

          {membersCount >= WORKSPACE_MEMBER_LIMIT_BY_PLAN[SubscriptionPlan.PRO_PER_SEAT] && !isWorkspaceEnterprise &&
            <BlockInfo className={styles.maxMembersWarning}>
              <Trans
                t={t}
                i18nKey="add_workspace_members_list:max_members_warning"
                values={{ maxMembers: WORKSPACE_MEMBER_LIMIT_BY_PLAN[SubscriptionPlan.PRO_PER_SEAT] }}
              >
                <a
                  href={getPricingUrl(i18n.language)}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                >
                  &nbsp;
                </a>
              </Trans>
            </BlockInfo>
          }

          <AddMembersRole allowedSeatTypes={allowedSeatTypes} selectedSeatType={effectiveSeatType} onChange={setSelectedSeatType} disabled={isInviteLocked} />

          {isWorkspacePersonal &&
            <BlockInfo>
              {t('personal_workspace_invite_hint')}
            </BlockInfo>
          }

        </Stack>

      </div>

    </Modal>
  )
}
